/**
 * Fetches weekly working hours for the selected interviewers and
 * returns the common availability windows (union for 1:1, intersection
 * for panel). Stays empty for self-schedule modes.
 */
import { useMemo } from 'react';
import { useInterviewerScheduleQuery } from '@/api/interviewerSchedule';
import {
  computeCommonAvailability,
  type InterviewKind,
  type Interval,
} from '../../availability';

export interface UseCommonAvailabilityArgs {
  weekStartIso: string;
  timezone: string;
  interviewerIds: number[];
  interviewKind: InterviewKind;
  enabled?: boolean;
}

export interface UseCommonAvailabilityResult {
  availability: Interval[];
  isLoading: boolean;
}

export function useCommonAvailability({
  weekStartIso,
  timezone,
  interviewerIds,
  interviewKind,
  enabled = true,
}: UseCommonAvailabilityArgs): UseCommonAvailabilityResult {
  const { data: scheduleByUserId = {}, isLoading } =
    useInterviewerScheduleQuery({
      weekStartIso,
      timezone,
      interviewerIds,
      enabled,
    });

  const availability = useMemo(
    () =>
      computeCommonAvailability({
        scheduleByUserId,
        timezone,
        interviewKind,
      }),
    [scheduleByUserId, timezone, interviewKind]
  );

  return { availability, isLoading };
}
